import { BubbleMenuPlugin } from '@tiptap/extension-bubble-menu';
import { Extension, ReactRenderer } from '@tiptap/react';
import { RefAttributes } from 'react';
import { TableCellBubbleMenu, TableCellBubbleMenuHandle } from './TableCellBubbleMenu';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      tableCellMenu: {
         toggleTableCellMenu: () => ReturnType;
         showTableCellMenu: () => ReturnType;
         hideTableCellMenu: () => ReturnType;
      };
   }
}

interface TableCellMenuOptions {
   editorId: string | null;
}
interface TableCellMenuStorage {
   renderer: null | ReactRenderer<
      TableCellBubbleMenuHandle,
      RefAttributes<TableCellBubbleMenuHandle>
   >;
   bubbleMenu: null | ReturnType<typeof BubbleMenuPlugin>;
}

export const TableCellMenu = Extension.create<TableCellMenuOptions, TableCellMenuStorage>({
   name: 'tableCellMenu',
   onCreate() {
      this.storage.renderer = new ReactRenderer(TableCellBubbleMenu, {
         props: {},
         editor: this.editor,
      });

      this.storage.bubbleMenu = BubbleMenuPlugin({
         pluginKey: 'table-cell-bubble-menu-' + this.options.editorId,
         editor: this.editor,
         element: this.storage.renderer.element as HTMLElement,
         shouldShow: () => this.editor.isActive('table'),
         tippyOptions: {
            duration: [100, 0],
            appendTo: 'parent',
            maxWidth: 'none',
            moveTransition: 'transform 0.1s ease 0.05s',
            ignoreAttributes: true,
            placement: 'bottom',
         },
      });

      this.editor.registerPlugin(this.storage.bubbleMenu);
   },
   addOptions() {
      return {
         editorId: null,
      };
   },
   addStorage() {
      return {
         renderer: null,
         bubbleMenu: null,
      };
   },
   onExit() {
      this.storage.renderer.destroy();
      this.editor.unregisterPlugin(this.storage.bubbleMenu);
   },
   addKeyboardShortcuts() {
      return {
         'Mod-Space': () => this.editor.commands.toggleTableCellMenu(),
      };
   },
   addCommands() {
      return {
         toggleTableCellMenu:
            () =>
            ({ dispatch }) => {
               if (dispatch) {
                  return this.storage.renderer?.ref?.toggleVisibility() ?? false;
               }
               return true;
            },
         showTableCellMenu:
            () =>
            ({ dispatch }) => {
               if (dispatch) {
                  return this.storage.renderer?.ref?.show() ?? false;
               }
               return true;
            },
         hideTableCellMenu:
            () =>
            ({ dispatch }) => {
               if (dispatch) {
                  return this.storage.renderer?.ref?.hide() ?? false;
               }
               return true;
            },
      };
   },
});
